import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  Inbox, Clock, CheckCircle2, XCircle, AlertCircle, Eye, Loader2,
  FileText, ShoppingCart, Package, DollarSign
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ApprovalInbox = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [inbox, setInbox] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  
  // For demo purposes, using ADMIN user
  const userId = 'ADMIN';

  useEffect(() => {
    loadInbox();
  }, []);

  const loadInbox = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/release/inbox/${userId}`);
      setInbox(response.data);
    } catch (error) {
      console.error('Error loading inbox:', error);
      toast.error('Failed to load approval inbox');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (item) => {
    try {
      setProcessingId(item.instance_id);
      await axios.post(`${BACKEND_URL}/api/release/workflow/approve`, {
        instance_id: item.instance_id,
        release_code: item.release_code,
        action: "APPROVE",
        user_id: userId,
        comments: "Approved"
      });
      toast.success('Approved successfully');
      loadInbox();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (item) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;
    
    try {
      setProcessingId(item.instance_id);
      await axios.post(`${BACKEND_URL}/api/release/workflow/approve`, {
        instance_id: item.instance_id,
        release_code: item.release_code,
        action: "REJECT",
        user_id: userId,
        rejection_reason: reason
      });
      toast.success('Rejected');
      loadInbox();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessingId(null);
    }
  };

  const getObjectIcon = (type) => {
    switch (type) {
      case 'PR': return <FileText className="w-5 h-5 text-orange-600" />;
      case 'PO': return <ShoppingCart className="w-5 h-5 text-green-600" />;
      case 'SO': return <Package className="w-5 h-5 text-orange-600" />;
      default: return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  const getObjectTypeBadge = (type) => {
    const colors = {
      'PR': 'bg-orange-100 text-orange-800',
      'PO': 'bg-green-100 text-green-800',
      'SO': 'bg-orange-100 text-orange-800',
      'RFQ': 'bg-orange-100 text-orange-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="approval-inbox">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Inbox className="w-7 h-7 text-orange-600" />
              Approval Inbox
            </h1>
            <p className="text-sm text-gray-500">Documents pending your approval</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/release/strategies')}>
            Configure Strategies
          </Button>
        </div>

        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading inbox...</p>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <p className="text-sm text-gray-600">Total Pending</p>
                <p className="text-3xl font-bold text-orange-600">{inbox?.total_pending || 0}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <p className="text-sm text-gray-600">Overdue</p>
                <p className="text-3xl font-bold text-red-600">{inbox?.overdue_count || 0}</p>
              </div>
              {Object.entries(inbox?.pending_by_type || {}).slice(0, 2).map(([type, count]) => (
                <div key={type} className="bg-white rounded-lg shadow-sm border p-4">
                  <p className="text-sm text-gray-600">{type}</p>
                  <p className="text-3xl font-bold text-gray-900">{count}</p>
                </div>
              ))}
            </div>

            {/* Inbox Items */}
            <div className="bg-white rounded-lg shadow-sm border">
              {!inbox?.items?.length ? (
                <div className="p-8 text-center">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-400" />
                  <p className="text-gray-600 font-medium">All caught up!</p>
                  <p className="text-sm text-gray-500">No documents pending your approval</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {inbox.items.map((item) => (
                    <div key={item.instance_id} className={`p-4 hover:bg-gray-50 ${item.priority === 'HIGH' ? 'bg-red-50' : ''}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="mt-1">{getObjectIcon(item.object_type)}</div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getObjectTypeBadge(item.object_type)}`}>
                                {item.object_type}
                              </span>
                              <span className="font-semibold text-gray-900">{item.document_number}</span>
                              {item.priority === 'HIGH' && (
                                <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs font-medium flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" />
                                  Overdue
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{item.document_description || 'No description'}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {item.days_pending} days pending
                              </span>
                              {item.total_value && (
                                <span className="flex items-center gap-1">
                                  <DollarSign className="w-4 h-4" />
                                  {formatCurrency(item.total_value, item.currency)}
                                </span>
                              )}
                              <span>Step: {item.release_code_description} ({item.sequence})</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReject(item)}
                            disabled={processingId === item.instance_id}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleApprove(item)}
                            disabled={processingId === item.instance_id}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {processingId === item.instance_id ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                            )}
                            Approve
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default ApprovalInbox;
